import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api, apiUrl } from '../api/client.js';
import { useMe } from '../api/hooks.js';
import { Badge, Card, ConfirmDialog, PageTitle, SectionLabel } from '../components/ui.js';
import { Button } from '../components/ui/Button.js';
import { Select } from '../components/ui/Input.js';
import { daysUntilExpiry, getAiToken, getSessionToken, setAiToken } from '../lib/auth.js';
import { copyText } from '../lib/format.js';

const MCP_URL = `${apiUrl('/mcp')}`;
const V1_URL = `${apiUrl('/v1')}`;
const TTL = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days (recommended)' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' },
  { value: 3650, label: 'Unlimited (10 years)' },
];

function cursorSnippet(url: string, token: string) {
  return JSON.stringify(
    { mcpServers: { loom: { url, ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}) } } },
    null,
    2,
  );
}

function claudeCmdSnippet(url: string, token: string) {
  const header = token ? ` \\\n  --header "Authorization: Bearer ${token}"` : '';
  return `claude mcp add loom \\\n  --transport http \\\n  --url ${url}${header}`;
}

function claudeJsonSnippet(url: string, token: string) {
  return JSON.stringify(
    { loom: { type: 'http', url, ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}) } },
    null,
    2,
  );
}

function claudeDesktopSnippet() {
  return JSON.stringify(
    { mcpServers: { loom: { command: 'npx', args: ['mcp-remote', MCP_URL] } } },
    null,
    2,
  );
}

function claudeDesktopPySnippet(token: string) {
  const tok = token.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  return `python3 -c "
import json, pathlib

token = '${tok}'
config_path = None

for name in ['Claude-3p', 'Claude']:
    p = pathlib.Path.home() / 'Library/Application Support' / name / 'claude_desktop_config.json'
    if p.parent.exists():
        cfg = json.loads(p.read_text()) if p.exists() else {}
        cfg['mcpServers'] = {
            'loom': {
                'command': 'npx',
                'args': [
                    'mcp-remote',
                    '${MCP_URL}',
                    '--header',
                    f'Authorization: Bearer {token}'
                ]
            }
        }
        p.write_text(json.dumps(cfg, indent=2))
        print(f'Done! Written to {p}')
        print('Fully quit Claude Desktop (menu → Quit), then reopen.')
        config_path = p
        break

if not config_path:
    print('Claude Desktop not found. Is it installed?')
"`;
}

function codexSnippet(url: string, token: string) {
  return [
    `# ~/.codex/config.toml`,
    `[mcp_servers.loom]`,
    `url = "${url}"`,
    ...(token ? [`http_headers = { Authorization = "Bearer ${token}" }`] : []),
  ].join('\n');
}

function genericSnippet(url: string, token: string) {
  return [
    `URL:       ${url}`,
    `Transport: Streamable HTTP (POST/GET/DELETE)`,
    `Protocol:  MCP 2024-11-05`,
    token ? `Auth:      Authorization: Bearer ${token}` : `Auth:      (none — dev mode)`,
  ].join('\n');
}

function v1CursorSnippet(base: string, token: string) {
  const key = token || '<AI JWT from above>';
  return [
    `OpenAI Base URL: ${base}/v1`,
    `OpenAI API Key:  ${key}`,
    `Model:           openrouter/openrouter/free`,
    ``,
    `# Or list models:`,
    `# GET ${base}/v1/models  (Authorization: Bearer ${key})`,
  ].join('\n');
}

function v1OpenWebuiSnippet(base: string, token: string) {
  const key = token || '<AI JWT from above>';
  return [
    `Settings → Connections → OpenAI-compatible`,
    `API Base URL: ${base}/v1`,
    `API Key:      ${key}`,
    `Model:        openrouter/openrouter/free  (or pick from GET /v1/models)`,
  ].join('\n');
}

function openWebuiMcpSnippet() {
  return [
    `MCP server URL: ${MCP_URL}`,
    `Auth:           OAuth (PKCE — browser login, no client secret)`,
    `Model brain:    use /v1 LLM section above (separate from MCP)`,
  ].join('\n');
}

export function Connect() {
  const me = useMe();
  const [ttl, setTtl] = useState(30);
  const [token, setToken] = useState(() => getAiToken() ?? '');
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (token) return;
    const session = getSessionToken();
    if (!session) return;
    void api
      .get<{ token: string | null }>('/api/ai-token')
      .then((data) => {
        if (data.token) {
          setAiToken(data.token);
          setToken(data.token);
        }
      })
      .catch(() => {
        /* no token yet */
      });
  }, [token]);

  const days = token ? daysUntilExpiry(token) : null;

  async function generate() {
    const data = await api.post<{ token: string; expiresInDays: number }>(`/api/tokens?days=${ttl}`);
    setAiToken(data.token);
    setToken(data.token);
    toast.success(`Token generated · ${data.expiresInDays} days`);
  }

  async function testConnection() {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await fetch(apiUrl('/mcp'), {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          accept: 'application/json, text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'initialize',
          params: {
            protocolVersion: '2024-11-05',
            capabilities: {},
            clientInfo: { name: 'loom-portal', version: '1.0' },
          },
        }),
      });
      if (r.ok) setTestResult('✓ MCP connection OK');
      else setTestResult(`✗ Failed (${r.status})`);
    } catch (err) {
      setTestResult((err as Error).message);
    } finally {
      setTesting(false);
    }
  }

  async function copy(label: string, text: string) {
    const ok = await copyText(text);
    toast[ok ? 'success' : 'error'](ok ? `Copied ${label}` : 'Copy failed');
  }

  return (
    <div>
      <PageTitle
        title="AI Config"
        sub="Generate your AI JWT for MCP tools and the /v1 LLM router. Save a provider on AI Models first, then use the /v1 LLM section below."
      />

      <Card className="mb-4 border border-[var(--color-star)]/30 bg-[var(--color-star)]/5 p-4 text-sm text-[var(--color-ink-muted)]">
        <strong className="text-[var(--color-star)]">/v1 LLM router — beta-ready</strong>
        <br />
        Cursor model brain · Open WebUI · Personal Bot replies · scripts. Requires a green{' '}
        <strong>Ready</strong> provider on{' '}
        <Link to="/models" className="text-[var(--color-accent)]">
          AI Models
        </Link>{' '}
        before chat works. Use the <strong>AI JWT</strong> from this page — not your OpenRouter key.
      </Card>

      <Card className="mb-4 flex items-center gap-3 p-4">
        <div className="flex-1">
          <div className="text-[11px] tracking-wide text-[var(--color-ink-faint)] uppercase">
            Loom MCP endpoint
          </div>
          <div className="font-mono text-sm text-[var(--color-accent)]">{MCP_URL}</div>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void copy('URL', MCP_URL)}>
          Copy
        </Button>
      </Card>

      {days !== null && days <= 7 ? (
        <p className="mb-4 text-sm text-[var(--color-star)]">
          {days <= 0 ? 'This AI token has expired.' : `Expires in ${days} day${days === 1 ? '' : 's'}.`}
        </p>
      ) : null}

      {!token ? (
        <Card className="mb-5 p-6 text-center">
          <div className="text-base font-semibold">Generate your first AI token</div>
          <p className="mx-auto mt-2 max-w-md text-sm text-[var(--color-ink-muted)]">
            Connect Cursor, Claude Desktop, Codex, or any AI tool to Loom MCP.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <Select value={String(ttl)} onChange={(e) => setTtl(Number(e.target.value))} className="w-auto">
              {TTL.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
            <Button onClick={() => void generate().catch((err: Error) => toast.error(err.message))}>
              Generate token
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="mb-4 flex flex-wrap items-center gap-3 p-4">
          <div className="flex-1">
            <div className="text-[11px] tracking-wide text-[var(--color-ink-faint)] uppercase">Active token</div>
            <div className="text-sm font-medium">{me.data?.name ?? me.data?.email}</div>
            <div className="text-xs text-[var(--color-ink-faint)]">
              {days !== null ? `${days} days remaining` : ''}
            </div>
          </div>
          <Badge tone="good">{me.data?.role ?? 'member'}</Badge>
          <Button variant="secondary" size="sm" onClick={() => void copy('token', token)}>
            Copy token
          </Button>
          <Link to="/connections" className="text-sm text-[var(--color-accent)]">
            My Connections
          </Link>
        </Card>
      )}

      <Card className="mb-4 flex flex-wrap items-center gap-2 p-4">
        <span className="text-[11px] tracking-wide text-[var(--color-ink-faint)] uppercase">Validity</span>
        <Select value={String(ttl)} onChange={(e) => setTtl(Number(e.target.value))} className="w-auto">
          {TTL.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <Button
          size="sm"
          onClick={() => (token ? setConfirmRegen(true) : void generate().catch((err: Error) => toast.error(err.message)))}
        >
          {token ? 'Generate new token' : 'Generate token'}
        </Button>
      </Card>

      {token ? (
        <div className="mb-6 flex items-center gap-3">
          <Button variant="secondary" onClick={() => void testConnection()} disabled={testing}>
            {testing ? 'Testing…' : '🔌 Test MCP Connection'}
          </Button>
          {testResult ? (
            <span
              className={`text-sm ${testResult.startsWith('✓') ? 'text-[var(--color-good)]' : 'text-[var(--color-danger)]'}`}
            >
              {testResult}
            </span>
          ) : null}
        </div>
      ) : null}

      <SectionLabel>/v1 LLM — Cursor · Open WebUI · Scripts</SectionLabel>
      <Card className="mb-6 p-4">
        <div className="mb-3 text-sm font-semibold">OpenAI-compatible model router</div>
        <p className="mb-3 text-xs text-[var(--color-ink-muted)]">
          Prerequisite: save a provider on{' '}
          <Link to="/models" className="text-[var(--color-accent)]">
            AI Models
          </Link>{' '}
          until status is green <strong>Ready</strong>. Model IDs use a provider prefix — copy ★
          recommended chips from AI Models or <code className="text-[10px]">GET /v1/models</code>.
        </p>
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-[var(--color-line)] p-3">
          <div className="flex-1">
            <div className="text-[10px] tracking-wide text-[var(--color-ink-faint)] uppercase">
              OpenAI Base URL
            </div>
            <div className="font-mono text-sm text-[var(--color-accent)]">{V1_URL}</div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => void copy('Base URL', V1_URL)}>
            Copy
          </Button>
        </div>
        {[
          {
            title: 'Cursor Settings → Models',
            text: v1CursorSnippet(apiUrl(''), token),
          },
          {
            title: 'Open WebUI → Settings → Connections → OpenAI',
            text: v1OpenWebuiSnippet(apiUrl(''), token),
          },
        ].map((s) => (
          <div key={s.title} className="mb-3 overflow-hidden rounded-lg border border-[var(--color-line)]">
            <div className="flex items-center justify-between border-b border-[var(--color-line)] px-3 py-1.5">
              <span className="font-mono text-[11px] text-[var(--color-ink-faint)]">{s.title}</span>
              <Button variant="ghost" size="sm" onClick={() => void copy(s.title, s.text)}>
                Copy
              </Button>
            </div>
            <pre className="bg-[var(--color-surface-2)] p-3 font-mono text-xs text-[var(--color-ink-muted)]">
              {token ? s.text : 'Generate a token to fill this snippet.'}
            </pre>
          </div>
        ))}
      </Card>

      <SectionLabel>Open WebUI — MCP tools</SectionLabel>
      <Card className="mb-6 overflow-hidden">
        <div className="flex items-center justify-between border-b border-[var(--color-line)] px-3 py-1.5">
          <span className="font-mono text-[11px] text-[var(--color-ink-faint)]">Open WebUI MCP connection</span>
          <Button variant="ghost" size="sm" onClick={() => void copy('Open WebUI MCP', openWebuiMcpSnippet())}>
            Copy
          </Button>
        </div>
        <pre className="bg-[var(--color-surface-2)] p-3 font-mono text-xs text-[var(--color-ink-muted)]">
          {openWebuiMcpSnippet()}
        </pre>
      </Card>

      {[
        { title: 'Cursor', file: '~/.cursor/mcp.json', hint: 'Add to mcpServers', text: cursorSnippet(MCP_URL, token) },
        { title: 'Claude Code (CLI)', file: 'Terminal — MCP', hint: 'MCP tools only — use your Claude subscription for the model.', text: claudeCmdSnippet(MCP_URL, token) },
        { title: 'Claude Code JSON', file: '~/.claude.json → mcpServers', hint: 'Or add manually', text: claudeJsonSnippet(MCP_URL, token) },
        { title: 'Claude Desktop (OAuth)', file: 'mcp-remote', hint: 'Recommended — Claude Desktop opens Authentik on first run.', text: claudeDesktopSnippet() },
        { title: 'Claude Desktop (JWT fallback)', file: 'Terminal one-liner', hint: 'Bakes the AI JWT into the Desktop config.', text: claudeDesktopPySnippet(token) },
        { title: 'Codex CLI', file: '~/.codex/config.toml', hint: 'MCP tools only.', text: codexSnippet(MCP_URL, token) },
        { title: 'Generic HTTP MCP', file: 'Connection info', hint: 'Streamable HTTP transport.', text: genericSnippet(MCP_URL, token) },
      ].map((s) => (
        <div key={s.file} className="mb-4">
          <SectionLabel>{s.title}</SectionLabel>
          <p className="mb-2 text-xs text-[var(--color-ink-faint)]">{s.hint}</p>
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-[var(--color-line)] px-3 py-1.5">
              <span className="font-mono text-[11px] text-[var(--color-ink-faint)]">{s.file}</span>
              <Button variant="ghost" size="sm" onClick={() => void copy(s.title, s.text)}>
                Copy
              </Button>
            </div>
            <pre className="bg-[var(--color-surface-2)] p-3 font-mono text-xs text-[var(--color-ink-muted)]">
              {token || s.file.includes('mcp-remote') ? s.text : 'Generate a token to fill this snippet.'}
            </pre>
          </Card>
        </div>
      ))}

      {confirmRegen ? (
        <ConfirmDialog
          title="Generate a new token?"
          body="The old token will stop working immediately. Update Cursor, Claude Desktop, and Codex. If you use a Mattermost Personal Bot, re-save it from My Connections."
          warn="Old token will stop working immediately."
          confirmText="Generate new token"
          onClose={() => setConfirmRegen(false)}
          onConfirm={() => {
            setConfirmRegen(false);
            void generate().catch((err: Error) => toast.error(err.message));
          }}
        />
      ) : null}
    </div>
  );
}
